import { Component, ReactNode } from 'react';
import Alert from '@cloudscape-design/components/alert';
import Container from '@cloudscape-design/components/container';
import Button from '@cloudscape-design/components/button';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Box from '@cloudscape-design/components/box';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
}

/**
 * Error Boundary Component
 * Catches JavaScript errors anywhere in the child component tree,
 * logs the error, and displays a fallback UI.
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error details
    this.setState({ errorInfo });
    
    // In production, you would send this to an error tracking service
    // Example: Sentry.captureException(error, { extra: errorInfo });
    if (import.meta.env.DEV) {
      console.error('Error caught by ErrorBoundary:', error, errorInfo);
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <Container>
          <SpaceBetween size="l">
            <Alert
              type="error"
              header="Something went wrong"
              action={
                <SpaceBetween direction="horizontal" size="xs">
                  <Button onClick={this.handleReset}>Try again</Button>
                  <Button onClick={this.handleReload}>Reload page</Button>
                </SpaceBetween>
              }
            >
              <SpaceBetween size="s">
                <Box variant="p">
                  We're sorry, but something unexpected happened. Please try again or reload the page.
                </Box>
                {import.meta.env.DEV && this.state.error && (
                  <Box variant="code">
                    <Box variant="strong">Error:</Box> {this.state.error.message}
                    {this.state.error.stack && (
                      <Box variant="pre" fontSize="body-s" color="text-body-secondary">
                        {this.state.error.stack}
                      </Box>
                    )}
                  </Box>
                )}
              </SpaceBetween>
            </Alert>
          </SpaceBetween>
        </Container>
      );
    }

    return this.props.children;
  }
}

